<template>
  <div id="side_left" :class="{is_active: isSidebarOpen}">
    <sidebar-user
      :name="userFullName"
      :role="$auth.user().position"
    />
    <div class="floating_navigation">
      <sidebar-tab
        :title="translations.overview"
        :subtitle="overviewSubTitle"
        :class="{active: this.activeSidebarTab === LAYOUT_ITEM_OVERVIEW}"
        @click="sidebarTabClick(LAYOUT_ITEM_OVERVIEW)"
      >
        <sidebar-progressbar
          :progress="overviewProgress"
          v-if="overviewProgress !== null"
        />
      </sidebar-tab>
      <nav-list
        v-for="(navList, index) in allowedNavItems"
        :key="index"
        :list="navList"
      />
    </div>
    <slot />
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import {
  LAYOUT_SET_SIDEBAR_OPEN,
  LAYOUT_SET_SIDEBAR_TAB_OPEN,
  LAYOUT_ITEM_STATION_PICKER,
  LAYOUT_ITEM_OVERVIEW
} from 'store/modules/layout'
import SidebarUser from './SidebarUser'
import SidebarTab from './SidebarTab'
import SidebarProgressbar from './SidebarProgressbar'
import NavList from './NavList'

const ROLE_ADMIN = 'ROLE_ADMIN'

export default {
  name: 'side-bar',
  components: {
    SidebarUser,
    SidebarTab,
    SidebarProgressbar,
    NavList
  },
  data() {
    return {
      LAYOUT_ITEM_STATION_PICKER,
      LAYOUT_ITEM_OVERVIEW
    }
  },
  created() {
    if (!this.broadcastOverview.briefOverview) {
      this.getBroadcastOverview()
    }
  },
  computed: {
    ...mapState({
      enums: state => state.enums.enums,
      enumsMap: state => state.enums.enumsMap,
      isSidebarOpen: state => state.layout.isSidebarOpen,
      activeSidebarTab: state => state.layout.activeSidebarTab,
      selectedStations: state => state.layout.selectedStations,
      broadcastOverview: state => state.broadcasts.broadcastOverview,
      allTvStations: state => state.tvStations.allTvStations
    }),
    translations() {
      return {
        overview: this.$gettext('Přehled')
      }
    },
    allowedNavItems() {
      const groups = []

      this.navItems.forEach(group => {
        const subGroup = []
        group.forEach(item => {
          if (!item.role || this.$auth.check(item.role)) {
            subGroup.push(item)
          }
        })
        if (subGroup.length !== 0) {
          groups.push(subGroup)
        }
      })

      return groups
    },
    userFullName() {
      const user = this.$auth.user()
      return `${user.firstName} ${user.lastName}`
    },
    overviewProgress() {
      return this.broadcastOverview.briefOverview
        ? this.broadcastOverview.briefOverview.overallProcessedPercentage : null
    },
    navItems() {
      return [
        [
          {
            text: this.$gettext('Zpracování EPG'),
            pathName: 'epgs',
            class: 'zpracovani_epg',
            hasSubGroup: false
          },
          {
            text: this.$gettext('TV program'),
            pathName: 'program',
            class: 'tv_program',
            hasSubGroup: false
          },
          {
            text: this.$gettext('Pořady'),
            pathName: 'shows',
            class: 'porady',
            hasSubGroup: false
          }
        ], [
          {
            name: 'cmsConfiguration',
            class: 'konfigurace',
            text: this.$gettext('Konfigurace CMS'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
              {
                text: this.$gettext('REGEXY'),
                pathName: 'templates',
                class: 'parovaci_sablony'
              },
              {
                text: this.$gettext('Štítky'),
                pathName: 'labels',
                class: 'stitky'
              },
              {
                text: this.$gettext('Jazyky'),
                pathName: 'languages',
                class: 'jazyky'
              },
              {
                text: this.$gettext('Státy'),
                pathName: 'countries',
                class: 'staty'
              }
            ]
          },
          {
            name: 'stationAdministration',
            class: 'stanice',
            text: this.$gettext('Správa stanic'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
              {
                text: this.$gettext('Skupiny stanic'),
                pathName: 'station-groups',
                class: 'skupiny_stanic'
              },
              {
                text: this.$gettext('TV stanice'),
                pathName: 'tv-stations',
                class: 'stanice'
              },
              {
                text: this.$gettext('TV kanály'),
                pathName: 'tv-channels',
                class: 'kanaly'
              },
              {
                text: this.$gettext('Rozlišení'),
                pathName: 'video-qualities',
                class: 'rozliseni'
              }
            ]
          },
          {
            name: 'import',
            class: 'importy',
            text: this.$gettext('Importy'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
              {
                text: this.$gettext('Zdroje EPG'),
                pathName: 'epg-sources',
                class: 'zdroje_epg'
              },
              {
                text: this.$gettext('Crony'),
                pathName: 'crons',
                class: 'crony'
              }
            ]
          },
          {
            name: 'settings',
            class: 'nastaveni',
            text: this.$gettext('Nastavení'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
              {
                text: this.$gettext('Rozměry obrázků'),
                pathName: 'image-sizes',
                class: 'rozmery_obrazku'
              },
              {
                text: this.$gettext('Věkové hranice'),
                pathName: 'maturityRatings',
                class: 'maturity_ratings'
              },
              {
                text: this.$gettext('Uživatelé'),
                pathName: 'users',
                class: 'uzivatele'
              },
              {
                text: this.$gettext('Nastavení'),
                pathName: 'settings',
                class: 'nastaveni'
              }
            ]
          },
          {
            name: 'currencies',
            class: 'nastaveni',
            text: this.$gettext('VOD'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
                {
                    text: this.$gettext('Poskytovatelé'),
                    pathName: 'vodProviders',
                    class: 'uzivatele',
                },
                {
                    text: this.$gettext('Balíčky'),
                    pathName: 'vodPackages',
                    class: 'nastaveni',
                },
                {
                    text: this.$gettext('Videa'),
                    pathName: 'vodVideos',
                    class: 'nastaveni',
                },
                {
                    text: this.$gettext('Měny'),
                    pathName: 'vodCurrencies',
                    class: 'nastaveni',
                }
            ]
          },
          {
            name: 'contentSettings',
            class: 'nastaveni',
            text: this.$gettext('Nastavení obsahu'),
            hasSubGroup: true,
            role: ROLE_ADMIN,
            items: [
              {
                text: this.$gettext('Vazby na Backoffice'),
                pathName: 'backoffices',
              },
              {
                text: this.$gettext('Blokovaný obsah'),
                pathName: 'blackoutContents',
              },
              {
                text: this.$gettext('Šablony blokování'),
                pathName: 'blackoutContentTemplates',
              },
            ]
          }
        ]
      ]
    },
    overviewSubTitle() {
      const language = this.$language.current

      if (!this.broadcastOverview.briefOverview) {
        return this.$gettext('Neznámý rozsah')
      }

      const data = this.broadcastOverview.briefOverview

      const currentDate = moment()
      const fromMomentJSTime = moment.unix(data.fromTimestamp)
      const toMomentJSTime = moment.unix(data.toTimestamp)

      if (language === 'ces' || language === 'slk') {
        const dateToTextDayFirst = (source) => source.format(currentDate.isSame(source, 'day') ? '[Dnes] D.M.' : 'ddd D.M.')

        return dateToTextDayFirst(fromMomentJSTime) + '–' + dateToTextDayFirst(toMomentJSTime)
      } else {
        const dateToTextMonthFirst = (source) => source.format(currentDate.isSame(source, 'day') ? '[Dnes] M/D' : 'ddd M/D')

        return dateToTextMonthFirst(fromMomentJSTime) + '–' + dateToTextMonthFirst(toMomentJSTime)
      }
    }
  },
  methods: {
    ...mapActions(['getBroadcastOverview', 'getTvStations']),
    sidebarTabClick(name) {
      this.$store.commit({
        type: LAYOUT_SET_SIDEBAR_TAB_OPEN,
        name: this.activeSidebarTab === name ? '' : name
      })
    }
  },
  watch: {
    $route() {
      if (this.isSidebarOpen && window.innerWidth < 729) {
        this.$store.commit({
          type: LAYOUT_SET_SIDEBAR_OPEN,
          open: false
        })
      }
      if (this.activeSidebarTab) {
        this.$store.commit({
          type: LAYOUT_SET_SIDEBAR_TAB_OPEN,
          name: ''
        })
      }
    }
  }
}
</script>
